import type { AnyFieldDef, ColumnSchema, OperatorId } from '@resource-kit/model';
import type { ColumnKind, ColumnTypeMap } from './serialize';
import type { FilterWhitelist } from '../list/whitelist';
import { toFilterType, type FilterTypeSource } from '../widgets/filter-type';
import { resolveEnumItems, type EnumItemsSource } from '../util/enum-items';

/**
 * The members `deriveQueryColumnsFromFields` reads off a field.
 *
 * Structural on purpose (#2167 PR-3, invariant 7). It used to take
 * `EntityField<T>`, which is a REACT contract — `render` is a `ComponentType` —
 * and naming it from a root-half module would make `import
 * '@resource-kit/controllers'` a framework binding. Every member type is copied
 * verbatim from `EntityField`'s member of the same name (`react/contracts.ts`),
 * never widened, so `EntityField<T>` still satisfies it and no call site
 * changed.
 *
 * The forwarded members are not re-declared. This EXTENDS the parameter types
 * of the two resolvers it hands the field to — `FilterTypeSource` for
 * `toFilterType`, `EnumItemsSource` for `resolveEnumItems` — the same device
 * `ValidatableField` uses
 * (`packages/resource-kit-controllers/form/validation.ts::ValidatableField`). Only
 * the three members
 * this module reads itself are declared below: `key` and `filterId` build the
 * column id and `label` is copied onto the schema row, both inside
 * `deriveQueryColumnsFromFields` in this file.
 *
 * `key` is `string` rather than `EntityField`'s per-branch
 * `Extract<keyof T, string>` / `string`, which IS that union: the entity
 * branch's key is a `keyof T` narrowed to strings, the virtual branch's is
 * free-form.
 */
export interface FilterSourceField extends FilterTypeSource, EnumItemsSource {
  key: string;
  label: string;
  filterId?: string;
}

/**
 * The minimal shape `deriveQueryBuilderColumns` reads off a TanStack
 * `ColumnDef`: the schema-derived filter metadata (`meta.filter`) and the
 * column's identity (`accessorKey` / `id`). Kept structural so the derive
 * step is a pure, unit-testable function independent of the table runtime.
 */
interface FilterMetaColumn {
  accessorKey?: unknown;
  id?: unknown;
  header?: unknown;
  meta?: {
    filter?: {
      type?: ColumnSchema['type'];
      label?: string;
      fieldId?: string;
      enumValues?: ColumnSchema['enumValues'];
    };
  };
}

/**
 * COMPATIBILITY ENTRY for the non-framework `ListView` consumers
 * (the remaining hand-rolled bespoke tables) that build ColumnDefs
 * by hand and have no `EntityField` list. Framework pages via `ResourcePage`
 * derive the schema straight from the field definitions with
 * `deriveQueryColumnsFromFields` (#1407 R3) — that is the primary path; this
 * ColumnDef-based derivation is kept only for those bespoke tables.
 *
 * Build the QueryBuilder's `ColumnSchema[]` from the table's column defs.
 *
 * When the resource declares a backend `filterable` whitelist, this is the
 * single place that reconciles UI to backend (#contains-filter):
 *   - a column is offered as a filter ONLY if the whitelist lists its field
 *     (a field with no accepted op is not a filter — it is dropped), and
 *   - its `operations` are pinned to the whitelist's op set, so the op
 *     dropdown (via `getOperatorsFor`) never shows an op the backend would
 *     silently drop.
 *
 * With no whitelist (`filterable` undefined) every `meta.filter` column is
 * offered with no `operations` cap — the prior behavior, preserved for
 * bespoke tables that do not declare a backend whitelist.
 *
 * Returns `null` (not `[]`) when nothing is filterable, matching the
 * ListView contract that hides the filter bar on a falsy schema.
 */
export function deriveQueryBuilderColumns(
  columns: readonly unknown[],
  filterable?: FilterWhitelist,
): ColumnSchema[] | null {
  const list: ColumnSchema[] = [];
  for (const raw of columns as readonly FilterMetaColumn[]) {
    const meta = raw?.meta?.filter;
    if (!meta?.type) continue;
    const id =
      meta.fieldId ||
      (typeof raw.accessorKey === 'string'
        ? raw.accessorKey
        : typeof raw.id === 'string'
          ? raw.id
          : '');
    if (!id) continue;

    let operations: readonly OperatorId[] | undefined;
    if (filterable) {
      const ops = filterable[id];
      // Field absent from the whitelist, or whitelisted with no op, is not
      // filterable on the backend — do not offer it as a filter column.
      if (!ops || ops.length === 0) continue;
      operations = ops;
    }

    const label = meta.label || (typeof raw.header === 'string' ? raw.header : id);
    list.push({ id, label, type: meta.type, enumValues: meta.enumValues, operations });
  }
  return list.length > 0 ? list : null;
}

/**
 * Build the `field-id → column type` lookup the serializer needs to complete a
 * temporal filter bound (#2094), straight off the resource MODEL.
 *
 * Read off `model.fields` rather than the derived `ColumnSchema[]` because the
 * consumer (`listController` → `useDataTableParams` → `serializeConditionsToParams`)
 * sits ABOVE the view that derives those columns, and the model is the same
 * source that view derives from. Two details are load-bearing:
 *
 *   - the key is `filterId ?? <field name>` — the id a `Condition.field`
 *     actually carries. Keying on the field name alone would silently miss
 *     exactly the fields that declare a different backend key (`auditResource`),
 *     i.e. the map would look populated and do nothing;
 *   - the value comes from `toFilterType`, the SAME resolution order
 *     (`filterable: false` → `filterType` → widget → type registry) the
 *     QueryBuilder's own column derivation uses, so the type this map reports
 *     is the type the operator was offered.
 *
 * Fields that project to no filter type at all are omitted; an unmapped field
 * simply gets no completion, which is the pre-#2094 behavior.
 *
 * An instant column additionally resolves WHOSE calendar day a bare bound
 * names (#2105) — merchant by default, UTC where the field declares
 * `instantReading: 'utc'`. It is resolved here, once, rather than passed along
 * beside the type, because the serializer needs exactly one fact per field and
 * two members could contradict each other.
 */
export function deriveColumnTypes(fields: Readonly<Record<string, AnyFieldDef>>): ColumnTypeMap {
  const map: Record<string, ColumnKind> = {};
  for (const [key, field] of Object.entries(fields)) {
    const type = toFilterType(field);
    if (!type) continue;
    map[field.filterId ?? key] = type === 'datetime' ? instantKindOf(field) : type;
  }
  return map;
}

/**
 * Absent ⇒ merchant. The default belongs to the majority reading (an operator
 * filtering "the 13th" means their own business day) so the declaration exists
 * only where the exception does, and the census test is what makes a forgotten
 * exception loud rather than silent.
 */
function instantKindOf(field: AnyFieldDef): ColumnKind {
  return field.instantReading === 'utc' ? 'instant-utc' : 'instant-merchant';
}

/**
 * Build the QueryBuilder's `ColumnSchema[]` DIRECTLY from a resource's
 * `EntityField` list — the primary derivation for `ResourcePage` (#1407 R3).
 *
 * The ColumnDef path (`deriveQueryBuilderColumns`) reads `meta.filter`, a
 * projection the column builders create from these same fields, so
 * the filter schema was derived from a second-order artifact. That made an
 * `access.list: 'hidden'` column structurally unable to be a filter even when
 * the backend whitelists it. This reads the field directly and IGNORES
 * `access.list`: hidden columns can still be filtered (a capability, gated —
 * as always — by the backend `filterable` whitelist, not by list visibility).
 *
 * Whitelist intersection semantics are identical to `deriveQueryBuilderColumns`:
 *   - `field.filterable === false` (or a type with no filter projection) drops
 *     the field entirely;
 *   - with a whitelist, a field absent from it (or whitelisted with no op) is
 *     not offered, and its `operations` are pinned to the whitelist's op set;
 *   - with no whitelist every filterable field is offered uncapped.
 *
 * Returns `null` (not `[]`) when nothing is filterable — the ListView
 * contract that hides the filter bar on a falsy schema.
 */
export function deriveQueryColumnsFromFields(
  fields: readonly FilterSourceField[],
  filterable?: FilterWhitelist,
): ColumnSchema[] | null {
  const list: ColumnSchema[] = [];
  for (const field of fields) {
    // `toFilterType` honors `filterable: false` (opt-out), the `filterType`
    // override, and otherwise the FIELD_TYPE_REGISTRY projection — the same
    // source both column builders use, so the two paths never drift.
    const type = toFilterType(field);
    if (!type) continue;
    const id = field.filterId || field.key;
    if (!id) continue;

    let operations: readonly OperatorId[] | undefined;
    if (filterable) {
      const ops = filterable[id];
      // Field absent from the whitelist, or whitelisted with no op, is not
      // filterable on the backend — do not offer it as a filter column.
      if (!ops || ops.length === 0) continue;
      operations = ops;
    }

    const enumValues = resolveEnumItems(field).map(({ value, label }) => ({
      value: String(value),
      label,
    }));
    list.push({ id, label: field.label, type, enumValues, operations });
  }
  return list.length > 0 ? list : null;
}
